import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, imgSrc, formatApiErrorDetail } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Film, Image as ImageIcon, PenLine } from "lucide-react";

export default function MediaLibrary() {
  const { activeId, activeClient } = useClient();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/media", { params: { client_id: activeId } });
      setItems(data);
    } catch (err) {
      console.error("Failed to load media library:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const handleFiles = async (files) => {
    if (!activeId) return toast.error("Sélectionne d'abord une marque");
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      const form = new FormData();
      form.append("client_id", activeId);
      form.append("file", file);
      try {
        await api.post("/media/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch (e) {
        toast.error(`${file.name} : ${formatApiErrorDetail(e.response?.data?.detail)}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} média(s) importé(s)`);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce média ?")) return;
    try {
      await api.delete(`/media/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Média supprimé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendToComposer = (m) => {
    navigate("/composer", {
      state: { aiImage: { image_url: m.image_url, media_type: m.media_type } },
    });
  };

  return (
    <>
      <PageHeader
        overline={`Médiathèque · ${activeClient?.name || ""}`}
        title="Importer photos & vidéos"
      />

      <input
        ref={fileRef}
        data-testid="media-file-input"
        type="file"
        accept="image/*,video/*,.heic,.heif"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {/* Zone d'import */}
      <div
        data-testid="media-dropzone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        className="border-2 border-dashed border-white/15 rounded-sm p-10 text-center bg-secondary/40 mb-8"
      >
        <Upload className="h-8 w-8 mx-auto text-accent mb-3" />
        <p className="text-sm text-muted-foreground mb-4">
          Dépose ici les photos ou vidéos du client, ou clique pour parcourir tes fichiers.
        </p>
        <button
          data-testid="media-upload-button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || !activeId}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Importer des photos / vidéos
        </button>
        <p className="text-[11px] text-muted-foreground mt-3">
          Images (JPG, PNG…) et vidéos — max 100 Mo par fichier.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-10" data-testid="media-empty">
          Aucun média importé pour cette marque. Utilise le bouton ci-dessus.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {items.map((m) => (
            <div key={m.id} className="border border-white/10 rounded-sm overflow-hidden bg-secondary group">
              <div className="aspect-square bg-black/40 flex items-center justify-center relative">
                {m.media_type === "video" ? (
                  <video
                    src={imgSrc(m.image_url)}
                    className="w-full h-full object-cover"
                    controls
                    preload="metadata"
                  />
                ) : (
                  <img src={imgSrc(m.image_url)} alt="" className="w-full h-full object-cover" />
                )}
                <span className="absolute top-2 left-2 flex items-center gap-1 bg-black/70 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm">
                  {m.media_type === "video" ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                  {m.media_type === "video" ? "Vidéo" : "Photo"}
                </span>
              </div>
              <div className="flex">
                <button
                  data-testid={`media-use-${m.id}`}
                  onClick={() => sendToComposer(m)}
                  className="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors border-r border-white/10"
                >
                  <PenLine className="h-3.5 w-3.5" /> Post
                </button>
                <button
                  data-testid={`media-delete-${m.id}`}
                  onClick={() => remove(m.id)}
                  className="px-3 py-2 text-muted-foreground hover:text-accent transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
